// Performance monitoring utility
class PerformanceMonitor {
  constructor() {
    this.metrics = {
      apiCalls: {},
      cacheHits: 0,
      cacheMisses: 0,
      totalLoadTime: 0,
      averageLoadTime: 0,
      slowestEndpoint: null,
      fastestEndpoint: null,
    };
    
    this.startTimes = new Map();
    this.observers = new Set();
  }

  // Start timing an API call
  startTimer(endpoint) {
    const startTime = performance.now();
    this.startTimes.set(endpoint, startTime);
    return startTime;
  }

  // End timing an API call
  endTimer(endpoint) {
    const startTime = this.startTimes.get(endpoint);
    if (!startTime) return;

    const duration = performance.now() - startTime;
    this.startTimes.delete(endpoint);

    // Update metrics
    if (!this.metrics.apiCalls[endpoint]) {
      this.metrics.apiCalls[endpoint] = {
        count: 0,
        totalTime: 0,
        averageTime: 0,
        minTime: Infinity,
        maxTime: 0,
      };
    }

    const endpointMetrics = this.metrics.apiCalls[endpoint];
    endpointMetrics.count++;
    endpointMetrics.totalTime += duration;
    endpointMetrics.averageTime = endpointMetrics.totalTime / endpointMetrics.count;
    endpointMetrics.minTime = Math.min(endpointMetrics.minTime, duration);
    endpointMetrics.maxTime = Math.max(endpointMetrics.maxTime, duration);

    // Update global metrics
    this.metrics.totalLoadTime += duration;
    this.metrics.averageLoadTime = this.metrics.totalLoadTime / 
      Object.values(this.metrics.apiCalls).reduce((sum, m) => sum + m.count, 0);

    // Update slowest/fastest endpoints
    if (!this.metrics.slowestEndpoint || duration > this.metrics.slowestEndpoint.duration) {
      this.metrics.slowestEndpoint = { endpoint, duration };
    }
    if (!this.metrics.fastestEndpoint || duration < this.metrics.fastestEndpoint.duration) {
      this.metrics.fastestEndpoint = { endpoint, duration };
    }

    // Notify observers
    this.notifyObservers({
      type: 'apiCall',
      endpoint,
      duration,
      metrics: this.metrics,
    });

    return duration;
  }

  // Record cache hit
  recordCacheHit() {
    this.metrics.cacheHits++;
    this.notifyObservers({
      type: 'cacheHit',
      metrics: this.metrics,
    });
  }

  // Record cache miss
  recordCacheMiss() {
    this.metrics.cacheMisses++;
    this.notifyObservers({
      type: 'cacheMiss',
      metrics: this.metrics,
    });
  }

  // Get cache hit ratio
  getCacheHitRatio() {
    const total = this.metrics.cacheHits + this.metrics.cacheMisses;
    return total > 0 ? (this.metrics.cacheHits / total * 100).toFixed(1) : 0;
  }

  // Get performance summary
  getPerformanceSummary() {
    return {
      ...this.metrics,
      cacheHitRatio: this.getCacheHitRatio(),
      totalApiCalls: Object.values(this.metrics.apiCalls).reduce((sum, m) => sum + m.count, 0),
    };
  }

  // Add observer for performance events
  addObserver(observer) {
    this.observers.add(observer);
  }

  // Remove observer
  removeObserver(observer) {
    this.observers.delete(observer);
  }

  // Notify all observers
  notifyObservers(event) {
    this.observers.forEach(observer => {
      try {
        observer(event);
      } catch (error) {
        console.error('Error in performance observer:', error);
      }
    });
  }

  // Reset metrics
  reset() {
    this.metrics = {
      apiCalls: {},
      cacheHits: 0,
      cacheMisses: 0,
      totalLoadTime: 0,
      averageLoadTime: 0,
      slowestEndpoint: null,
      fastestEndpoint: null,
    };
    this.startTimes.clear();
  }

  // Export metrics for debugging
  exportMetrics() {
    const summary = this.getPerformanceSummary();
    console.table(summary);
    return summary;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Export the instance and a function to get it
export default performanceMonitor;

// Helper function to wrap API calls with performance monitoring
export const withPerformanceMonitoring = (apiCall, endpoint) => {
  return async (...args) => {
    performanceMonitor.startTimer(endpoint);
    try {
      const result = await apiCall(...args);
      performanceMonitor.endTimer(endpoint);
      return result;
    } catch (error) {
      performanceMonitor.endTimer(endpoint);
      throw error;
    }
  };
};

// Helper function to monitor cache operations
export const monitorCacheOperation = (isHit) => {
  if (isHit) {
    performanceMonitor.recordCacheHit();
  } else {
    performanceMonitor.recordCacheMiss();
  }
};
